'use strict';

import React from 'react';
import _ from 'underscore';
import url from 'url';
import memoize from 'memoize-one';

import { console, layout, searchFilters, analytics, memoizedUrlParse } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';
import { Schemas } from './../../util';
import { getTissueInternalCodeFromFacetTerm, getTissueCategoryFromFacetTerm, tissueCategoryOrder } from './../../util/data';
import DropdownItem from 'react-bootstrap/esm/DropdownItem';
import DropdownButton from 'react-bootstrap/esm/DropdownButton';
import * as vizUtil from '@hms-dbmi-bgm/shared-portal-components/es/components/viz/utilities';
import { Legend } from './../components';

/**
 * Component which wraps BarPlot.Chart and provides some UI buttons and stuff.
 * Passes props to BarPlot.Chart.
 */
export class UIControlsWrapper extends React.PureComponent {

    static TISSUE_FIELD = 'sample_summary.tissues';
    static TISSUE_CATEGORY_ALL = 'All';
    static TISSUE_CATEGORY_UNKNOWN = 'Unknown';
    static TISSUE_CATEGORY_ORDER = tissueCategoryOrder.concat([
        UIControlsWrapper.TISSUE_CATEGORY_UNKNOWN
    ]);
    static TISSUE_CATEGORY_CANONICAL_BY_LOWER = {
        'ectoderm': 'Ectoderm',
        'mesoderm': 'Mesoderm',
        'endoderm': 'Endoderm',
        'germ cells': 'Germ cells',
        'clinically accessible': 'Clinically accessible',
        'unknown': UIControlsWrapper.TISSUE_CATEGORY_UNKNOWN
    };

    static canShowChart(chartData) {
        if (!chartData) return false;
        if (!chartData.total) return false;
        if (chartData.total && chartData.total.files === 0) return false;
        if (chartData.total && chartData.total.donors === 0) return false;
        if (typeof chartData.field !== 'string') return false;
        if (typeof chartData.terms !== 'object') return false;
        if (_.keys(chartData.terms).length === 0) return false;
        return true;
    }

    static contextualView = memoize(function (href) {
        if (href) {
            // Hide on homepage. I guess could have used PageTitle.isHomePage here instead.
            const hrefParts = memoizedUrlParse(href);
            if (hrefParts.pathname === '/' || hrefParts.pathname === '/home') {
                return 'home';
            }
        }
        return 'browse';
    });

    static defaultProps = {
        titleMap: {
            // Aggr type
            files: 'Files',
            donors: 'Donors',

            // Show state
            all: 'All',
            filtered: 'Selected',
            both: 'All & Selected',
        },
        availableFields_XAxis: [
            // { title: 'Donor', field: 'donors.display_title' },
            { title: 'Tissue', field: 'sample_summary.tissues' },
            { title: 'Assay Type', field: 'assays.display_title' },
            { title: 'Sequencer', field: 'sequencers.display_title' },
        ],
        availableFields_Subdivision: [
            // { title: 'Donor', field: 'donors.display_title' },
            { title: 'Sequencer', field: 'sequencers.display_title' },
            { title: 'Assay Type', field: 'assays.display_title' },
            { title: 'Tissue', field: 'sample_summary.tissues' },
            // { title: 'Data Type', field: 'data_type' },
            // { title: 'File Format', field: 'file_format.display_title' },
            // { title: 'Data Category', field: 'data_category' },
            // { title: 'Software', field: 'software.display_title' },
        ],
        legend: false,
        chartHeight: 300,
        btnVariant: 'outline-secondary',
        subBarLayout: 'stacked',
    };

    static getDerivedStateFromProps({ barplot_data_filtered }, { showState }) {
        // Switch to 'all' if filtered data to show.
        // Inverse of this is done in componentDidUpdate if filtered data arrives for first time.
        if (
            showState === 'filtered' &&
            (!barplot_data_filtered ||
                (barplot_data_filtered &&
                    barplot_data_filtered.total.files === 0))
        ) {
            return { showState: 'all' };
        }
        return null;
    }

    constructor(props) {
        super(props);
        _.bindAll(this, 'filterObjExistsAndNoFiltersSelected', 'titleMap', 'adjustedChildChart',
            'getFieldAtIndex', 'renderDropDownMenuItems', 'handleDropDownToggle', 'renderShowTypeDropdown');

        this.handleDropDownShowTypeToggle = this.handleDropDownToggle.bind(this, 'showType');
        this.handleDropDownSubdivisionFieldToggle = this.handleDropDownToggle.bind(this, 'subdivisionField');
        this.handleDropDownYAxisFieldToggle = this.handleDropDownToggle.bind(this, 'yAxis');
        this.handleDropDownXAxisFieldToggle = this.handleDropDownToggle.bind(this, 'xAxisField');
        this.handleFirstFieldSelect = this.handleFieldSelect.bind(this, 0);
        this.handleSecondFieldSelect = this.handleFieldSelect.bind(this, 1);

        this.handleAggregateTypeSelect = _.throttle(this.handleAggregateTypeSelect.bind(this), 750);
        this.handleExperimentsShowType = _.throttle(this.handleExperimentsShowType.bind(this), 750, { trailing: false });
        this.handleFieldSelect = _.throttle(this.handleFieldSelect.bind(this), 300);

        this.state = {
            'aggregateType': props.mapping === 'all' ? 'files' : 'donors',
            'showState': this.filterObjExistsAndNoFiltersSelected() || (props.barplot_data_filtered && props.barplot_data_filtered.total.donors === 0) ? 'all' : 'filtered',
            'openDropdown': null,
            'tissueCategoryFilter': UIControlsWrapper.TISSUE_CATEGORY_ALL
        };
    }

    componentDidUpdate({ barplot_data_filtered: pastFilteredData }) {
        const { barplot_data_filtered: newFilteredData, barplot_data_unfiltered, barplot_data_fields } = this.props;
        this.setState(function ({ showState, tissueCategoryFilter }) {
            const nextState = {};
            // Set to filtered if new filtered data arrives.
            // Inverse of this done in getDerivedStateFromProps
            if (showState === "all" && newFilteredData &&
                newFilteredData.total.donors > 0 &&
                (!pastFilteredData || pastFilteredData.total.donors === 0)) {
                nextState.showState = 'filtered';
            }

            const isTissueXAxis = Array.isArray(barplot_data_fields) && barplot_data_fields[0] === UIControlsWrapper.TISSUE_FIELD;
            if (!isTissueXAxis && tissueCategoryFilter !== UIControlsWrapper.TISSUE_CATEGORY_ALL) {
                nextState.tissueCategoryFilter = UIControlsWrapper.TISSUE_CATEGORY_ALL;
            }

            if (isTissueXAxis) {
                const topLevelField = (showState === 'all' ? barplot_data_unfiltered : newFilteredData) || barplot_data_unfiltered;
                const availableCategories = UIControlsWrapper.getAvailableTissueCategories(topLevelField);
                if (tissueCategoryFilter !== UIControlsWrapper.TISSUE_CATEGORY_ALL && !availableCategories.includes(tissueCategoryFilter)) {
                    nextState.tissueCategoryFilter = UIControlsWrapper.TISSUE_CATEGORY_ALL;
                }
            }

            return Object.keys(nextState).length ? nextState : null;
        });
    }

    static getTissueCategoryByTermMap(barplotData) {
        return (barplotData && barplotData.meta && barplotData.meta.tissue_category_by_term) || {};
    }

    static normalizeTissueCategoryName(category) {
        if (!category || typeof category !== 'string') return null;
        const normalized = UIControlsWrapper.TISSUE_CATEGORY_CANONICAL_BY_LOWER[category.trim().toLowerCase()];
        return normalized || category;
    }

    static getTissueCategoryForTerm(termKey, tissueCategoryByTerm = {}) {
        const mapped = UIControlsWrapper.normalizeTissueCategoryName(tissueCategoryByTerm[termKey]);
        if (mapped) return mapped;
        const fallback = UIControlsWrapper.normalizeTissueCategoryName(getTissueCategoryFromFacetTerm(termKey));
        return fallback || UIControlsWrapper.TISSUE_CATEGORY_UNKNOWN;
    }

    static getAvailableTissueCategories(barplotData) {
        if (!barplotData || !barplotData.terms) return [UIControlsWrapper.TISSUE_CATEGORY_ALL];
        const tissueCategoryByTerm = UIControlsWrapper.getTissueCategoryByTermMap(barplotData);
        const available = new Set();
        _.forEach(_.keys(barplotData.terms), (termKey) => {
            available.add(UIControlsWrapper.getTissueCategoryForTerm(termKey, tissueCategoryByTerm));
        });
        const ordered = UIControlsWrapper.TISSUE_CATEGORY_ORDER.filter((layer) => available.has(layer));
        return [UIControlsWrapper.TISSUE_CATEGORY_ALL, ...ordered];
    }

    static getTermTotal(termObj, field) {
        if (!termObj) return 0;
        if (termObj.total && typeof termObj.total[field] === 'number') return termObj.total[field];
        if (typeof termObj[field] === 'number') return termObj[field];
        return 0;
    }

    static filterBarplotDataByTissueCategory(barplotData, tissueCategoryFilter) {
        if (!barplotData || !barplotData.terms) return barplotData;
        if (!tissueCategoryFilter || tissueCategoryFilter === UIControlsWrapper.TISSUE_CATEGORY_ALL) return barplotData;
        const tissueCategoryByTerm = UIControlsWrapper.getTissueCategoryByTermMap(barplotData);

        const filteredTerms = {};
        let totalFiles = 0;
        let totalDonors = 0;

        _.forEach(_.keys(barplotData.terms), (termKey) => {
            const termObj = barplotData.terms[termKey];
            if (UIControlsWrapper.getTissueCategoryForTerm(termKey, tissueCategoryByTerm) !== tissueCategoryFilter) return;
            filteredTerms[termKey] = termObj;
            totalFiles += UIControlsWrapper.getTermTotal(termObj, 'files');
            totalDonors += UIControlsWrapper.getTermTotal(termObj, 'donors');
        });

        return {
            ...barplotData,
            terms: filteredTerms,
            total: {
                ...(barplotData.total || {}),
                files: totalFiles,
                donors: totalDonors
            }
        };
    }

    getBarplotDataForTissueCategory() {
        const { barplot_data_unfiltered, barplot_data_filtered, barplot_data_fields } = this.props;
        const { tissueCategoryFilter } = this.state;
        const isTissueXAxis = Array.isArray(barplot_data_fields) && barplot_data_fields[0] === UIControlsWrapper.TISSUE_FIELD;
        if (!isTissueXAxis || tissueCategoryFilter === UIControlsWrapper.TISSUE_CATEGORY_ALL) {
            return { barplot_data_unfiltered, barplot_data_filtered };
        }
        return {
            barplot_data_unfiltered: UIControlsWrapper.filterBarplotDataByTissueCategory(barplot_data_unfiltered, tissueCategoryFilter),
            barplot_data_filtered: UIControlsWrapper.filterBarplotDataByTissueCategory(barplot_data_filtered, tissueCategoryFilter)
        };
    }

    // TODO: MAYBE REMOVE HREF WHEN SWITCH SEARCH FROM /BROWSE/
    filterObjExistsAndNoFiltersSelected() {
        const { donorFilters, href } = this.props;
        return searchFilters.filterObjExistsAndNoFiltersSelected(donorFilters) && !searchFilters.searchQueryStringFromHref(href);
    }

    titleMap(key = null, fromDropdown = false) {
        const { titleMap } = this.props, { aggregateType } = this.state;
        if (!key) return titleMap;
        let title = titleMap[key];
        if (fromDropdown && ['all', 'filtered'].indexOf(key) > -1) {
            title += ' ' + this.titleMap(aggregateType);
        } else if (fromDropdown && key == 'both') {
            return 'Both';
        }
        return title;
    }

    /**
     * Clones props.children, expecting a Chart React Component as the sole child, and extends Chart props with 'fields', 'showType', and 'aggregateType'.
     *
     * @todo validate that props.children is a BarPlot.Chart
     * @returns {React.Component} Cloned & extended props.children.
     */
    adjustedChildChart() {
        const { children, barplot_data_fields, termLabelTransform } = this.props;
        const { showState, aggregateType, tissueCategoryFilter } = this.state;
        const { barplot_data_unfiltered, barplot_data_filtered } = this.getBarplotDataForTissueCategory();
        const isTissueXAxis = Array.isArray(barplot_data_fields) && barplot_data_fields[0] === UIControlsWrapper.TISSUE_FIELD;
        const xAxisTermLabelMapper = (isTissueXAxis /*&& tissueCategoryFilter === UIControlsWrapper.TISSUE_CATEGORY_ALL*/)
            ? function (termKey, defaultLabel) {
                return getTissueInternalCodeFromFacetTerm(termKey) || defaultLabel;
            }
            : null;
        return React.cloneElement(children, _.extend(
            _.omit( // Own props minus these.
                this.props,
                'titleMap', 'availableFields_XAxis', 'availableFields_Subdivision', 'legend', 'chartHeight', 'children'
            ),
            {
                'fields': barplot_data_fields,
                'showType': showState,
                'aggregateType': aggregateType,
                'barplot_data_unfiltered': barplot_data_unfiltered,
                'barplot_data_filtered': barplot_data_filtered,
                'termLabelTransform': termLabelTransform,
                'xAxisTermLabelMapper': xAxisTermLabelMapper
            }
        ));
    }

    handleAggregateTypeSelect(eventKey, event) {
        this.setState({ 'aggregateType': eventKey });
    }

    handleExperimentsShowType(eventKey, event) {
        this.setState({ 'showState': eventKey });
    }

    /**
     * Handler for the Dropdown components which offer field options.
     *
     * @prop {function} updateBarPlotFields - Analog of ChartDataController.updateBarPlotFields, passed in by ChartDataController.Provider.
     * @prop {string[]} barplot_data_fields - List of currently aggregated fields; passed in by ChartDataController.Provider.
     *
     * @param {number} fieldIndex   Index of field in aggregation being changed -- must be 0 or 1.
     * @param {string} newFieldKey  Dot-delimited name of field used for aggregation, e.g. 'lab.display_title'.
     * @param {Event} [event]       Reference to event of DropDown change.
     */
    handleFieldSelect(fieldIndex, newFieldKey, event = null) {
        const { barplot_data_fields, updateBarPlotFields, availableFields_XAxis, availableFields_Subdivision, mapping } = this.props;
        let newFields;

        if (newFieldKey === "none") {
            // Only applies to subdivision (fieldIndex 1)
            newFields = barplot_data_fields.slice(0, 1);
            updateBarPlotFields(mapping, newFields);
            return;
        }

        const propToGetFieldFrom = fieldIndex === 0 ? availableFields_XAxis : availableFields_Subdivision;
        const newField = _.find(propToGetFieldFrom, { 'field': newFieldKey });
        const otherFieldIndex = fieldIndex === 0 ? 1 : 0;

        if (fieldIndex === 0 && barplot_data_fields.length === 1) {
            newFields = [null];
        } else {
            newFields = [null, null];
        }

        newFields[fieldIndex] = newField;
        if (newFields.length > 1) {
            const foundFieldFromProps = _.findWhere(
                availableFields_Subdivision.slice(0).concat(availableFields_XAxis.slice(0)),
                { 'field': barplot_data_fields[otherFieldIndex] }
            );
            newFields[otherFieldIndex] = foundFieldFromProps || {
                'title': barplot_data_fields[otherFieldIndex],
                'field': barplot_data_fields[otherFieldIndex]
            };
        }
        updateBarPlotFields(mapping, _.pluck(newFields, 'field'));
        //analytics
        analytics.event('cursor_detail', 'BarPlot', 'Set Aggregation Field', null, {
            'name': '[' + _.pluck(newFields, 'field').join(', ') + ']',
            'field_key': newFieldKey,
        });
    }

    getFieldAtIndex(fieldIndex) {
        const { barplot_data_fields, availableFields_XAxis, availableFields_Subdivision } = this.props;
        if (!barplot_data_fields) return null;
        if (!Array.isArray(barplot_data_fields)) return null;
        if (barplot_data_fields.length < fieldIndex + 1) return null;

        return (
            _.findWhere(availableFields_Subdivision.slice(0).concat(availableFields_XAxis.slice(0)), { 'field': barplot_data_fields[fieldIndex] })
        ) || {
            'title': barplot_data_fields[fieldIndex],
            'field': barplot_data_fields[fieldIndex]
        };
    }

    renderDropDownMenuItems(keys, active = null) {
        return _.map(keys, (menuKey) => {
            var [key, title = null, subtitle = null, disabled = false, tooltip = null] = Array.isArray(menuKey) ? menuKey : [menuKey];
            if (typeof title === 'string' && typeof tooltip === 'string') {
                title = <span className="d-inline-block" data-tip={tooltip} data-place="left">{title}</span>;
            }

            return (
                <DropdownItem key={key} eventKey={key} active={key === active} disabled={disabled}>
                    {title || this.titleMap(key, true)}
                </DropdownItem>
            );
        });
    }

    handleDropDownToggle(id, isOpen, evt, source) {
        if (isOpen) {
            setTimeout(() => {
                this.setState({ 'openDropdown': id });
            }, 10);
        } else {
            this.setState({ 'openDropdown': null });
        }
    }

    renderShowTypeDropdown() {
        const { href, barplot_data_filtered, btnVariant } = this.props;
        const { aggregateType, showState } = this.state;
        const contextualView = UIControlsWrapper.contextualView(href);
        if (contextualView === 'home') {
            return null;
        }
        // TODO: MAYBE REMOVE HREF WHEN SWITCH SEARCH FROM /BROWSE/
        const isSelectedDisabled = !!(
            (this.filterObjExistsAndNoFiltersSelected() && !searchFilters.searchQueryStringFromHref(href)) ||
            (barplot_data_filtered && barplot_data_filtered.total.files === 0)
        );
        const aggrTypeTitle = this.titleMap(aggregateType);
        const showStateTitle = showState === 'all' ? 'All' : 'Selected';
        return (
            <div className="show-type-change-section">
                <h6 className="dropdown-heading">
                    <span className="d-inline-block" data-tip={isSelectedDisabled ? "Enable some filters to enable toggling between viewing all and selected items." : null}>Show</span>
                </h6>
                <DropdownButton id="select-barplot-show-type"
                    onSelect={this.handleExperimentsShowType}
                    size="xs" variant={btnVariant}
                    disabled={isSelectedDisabled}
                    title={
                        <React.Fragment>
                            <span className="text-600">{showStateTitle}</span> {aggrTypeTitle}
                        </React.Fragment>
                    }
                    onToggle={this.handleDropDownShowTypeToggle}>
                    {
                        this.renderDropDownMenuItems([
                            [
                                'all',
                                <span key={0}>
                                    <span className="text-500">All</span> {this.titleMap(aggregateType)}
                                </span>
                            ],
                            [
                                'filtered',
                                <span key={1} className="d-inline-block" data-place="left" data-tip={isSelectedDisabled ? 'No filters currently set' : null}>
                                    <span className="text-500">Selected</span> {this.titleMap(aggregateType)}
                                </span>,
                                null,
                                isSelectedDisabled
                            ]
                        ], showState)
                    }
                </DropdownButton>
            </div>
        );
    }

    renderGroupByFieldDropdown() {
        const { isLoadingChartData, barplot_data_fields, availableFields_Subdivision, btnVariant } = this.props;
        let title;
        if (isLoadingChartData) {
            title = <span style={{ opacity: 0.33 }}><i className="icon icon-spin icon-circle-notch fas" /></span>;
        } else {
            const field = this.getFieldAtIndex(1);
            if (!field) title = "None";
            else title = field.title || Schemas.Field.toName(field.field);
        }
        return (
            <div className="field-1-change-section">
                <h6 className="dropdown-heading">Group By</h6>
                <DropdownButton id="select-barplot-field-1" onSelect={this.handleSecondFieldSelect}
                    disabled={isLoadingChartData} title={title} variant={btnVariant}
                    onToggle={this.handleDropDownSubdivisionFieldToggle} size="xs">
                    {
                        this.renderDropDownMenuItems(
                            _.map(availableFields_Subdivision.slice(0).concat([{
                                title: <em>None</em>,
                                field: "none"
                            }]), function (field) {
                                //const isDisabled = barplot_data_fields[0] === field.field;
                                return [
                                    field.field,                                        // Field
                                    field.title || Schemas.Field.toName(field.field),   // Title
                                    field.description || null,                          // Description
                                    //isDisabled,                                         // Disabled
                                    //isDisabled ? "Field already selected for X-Axis" : null
                                ]; // key, title, subtitle, disabled
                            }),
                            barplot_data_fields[1] || "none"
                        )
                    }
                </DropdownButton>
            </div>
        );
    }

    renderTissueCategoryFilter() {
        const { barplot_data_unfiltered, barplot_data_filtered, barplot_data_fields, isLoadingChartData, btnVariant } = this.props;
        const { showState, tissueCategoryFilter } = this.state;
        const isTissueXAxis = Array.isArray(barplot_data_fields) && barplot_data_fields[0] === UIControlsWrapper.TISSUE_FIELD;
        if (!isTissueXAxis) return null;

        const topLevelField = (showState === 'all' ? barplot_data_unfiltered : barplot_data_filtered) || barplot_data_unfiltered;
        const options = UIControlsWrapper.getAvailableTissueCategories(topLevelField);
        if (!options || options.length <= 1) return null;

        return (
            <div className="tissue-category-filter d-flex align-items-center justify-content-center flex-wrap gap-2 mb-2">
                <span className="text-muted small">Tissue Type</span>
                <div className="btn-group btn-group-sm" role="group" aria-label="Filter tissues by tissue type">
                    {options.map((category) => (
                        <button
                            key={category}
                            type="button"
                            className={`btn ${tissueCategoryFilter === category ? 'btn-secondary' : `btn-${btnVariant}`}`}
                            onClick={() => this.setState({ tissueCategoryFilter: category })}
                            disabled={isLoadingChartData}>
                            {category}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    render() {
        const {
            barplot_data_filtered, barplot_data_unfiltered, barplot_data_fields, isLoadingChartData, href, btnVariant,
            availableFields_XAxis, availableFields_Subdivision, schemas, chartHeight, windowWidth, cursorDetailActions,
            mapping = 'all'
        } = this.props;
        const { aggregateType, showState } = this.state;

        const tissueCategoryAdjusted = this.getBarplotDataForTissueCategory();
        const barplotDataFiltered = tissueCategoryAdjusted.barplot_data_filtered;
        const barplotDataUnfiltered = tissueCategoryAdjusted.barplot_data_unfiltered;

        if (!UIControlsWrapper.canShowChart(barplot_data_unfiltered)) return null;

        const windowGridSize = layout.responsiveGridState(windowWidth);
        const contextualView = UIControlsWrapper.contextualView(href);

        const legendContainerHeight = windowGridSize === 'xs' ? null
            : chartHeight - (49 * (contextualView === 'home' ? 1 : 2)) - 49;

        vizUtil.unhighlightTerms();

        let xAxisDropdownTitle;
        if (isLoadingChartData) {
            xAxisDropdownTitle = <span style={{ opacity: 0.33 }}><i className="icon icon-spin icon-circle-notch fas" /></span>;
        } else {
            const field = this.getFieldAtIndex(0);
            xAxisDropdownTitle = <span>{(field.title || Schemas.Field.toName(field.field))}</span>;
        }

        return (
            <div className="bar-plot-chart-controls-wrapper">
                <div className="overlay" style={{
                    width: (windowGridSize !== 'xs' ? (layout.gridContainerWidth(windowWidth) * (9 / 12) - 15) : null)
                }}>

                    <div className="y-axis-top-label" style={{
                        width: chartHeight,
                        top: chartHeight - 4
                    }}>
                        <div className="row" style={{ 'maxWidth': 210, 'float': 'right' }}>
                            <div className="col-3" style={{ 'width': 51 }}>
                                {/* <h6 className="dropdown-heading">Y Axis</h6> */}
                                <h6 className="dropdown-heading">{mapping === 'all' ? '# of Files' : '# of Donors'}</h6>
                            </div>
                            <div className="col-9" style={{ 'width': 100, 'textAlign': 'left', visibility: 'hidden' }}>
                                <DropdownButton
                                    id="select-barplot-aggregate-type"
                                    size="xs" variant={btnVariant}
                                    onSelect={this.handleAggregateTypeSelect}
                                    title={this.titleMap(aggregateType)}
                                    onToggle={this.handleDropDownYAxisFieldToggle}>
                                    {this.renderDropDownMenuItems(['files'], aggregateType)}
                                </DropdownButton>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="row">
                    <div className="col-12 col-md-9">{this.adjustedChildChart()}</div>
                    <div className="col-12 col-md-3 chart-aside" style={{ 'height': chartHeight }}>
                        <div className="x-axis-label">
                            <h6 className="dropdown-heading">X Axis</h6>
                            <DropdownButton id="select-barplot-field-0" onSelect={this.handleFirstFieldSelect}
                                disabled={isLoadingChartData} title={xAxisDropdownTitle} variant={btnVariant}
                                onToggle={this.handleDropDownXAxisFieldToggle} size="xs">
                                {
                                    this.renderDropDownMenuItems(
                                        _.map(availableFields_XAxis, function (field) {
                                            //const isDisabled = barplot_data_fields[1] && barplot_data_fields[1] === field.field;
                                            return [
                                                field.field,
                                                field.title || Schemas.Field.toName(field.field),
                                                field.description || null,
                                                //isDisabled,
                                                //isDisabled ? 'Field is already selected for "Group By"' : null
                                            ]; // key, title, subtitle
                                        }),
                                        barplot_data_fields[0]
                                    )
                                }
                            </DropdownButton>
                        </div>
                        {/* {this.renderShowTypeDropdown()} */}
                        {this.renderGroupByFieldDropdown()}
                        <div className="legend-container" style={{ 'height': legendContainerHeight }}>
                            <AggregatedLegend {...{ cursorDetailActions, aggregateType, schemas }}
                                height={legendContainerHeight}
                                barplot_data_filtered={barplotDataFiltered}
                                barplot_data_unfiltered={barplotDataUnfiltered}
                                // Keep legend labels aligned with chart bar/popover labels.
                                termLabelTransform={this.props.termLabelTransform}
                                field={_.findWhere(availableFields_Subdivision, { 'field': barplot_data_fields[1] }) || null}
                                showType={showState} />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-md-9 pt-4">
                        {this.renderTissueCategoryFilter()}
                    </div>
                </div>
            </div>
        );
    }

}

export class AggregatedLegend extends React.Component {

    static collectSubDivisionFieldTermCounts = memoize(function (rootField, aggregateType = 'files') {
        if (!rootField) return null;

        const retField = {
            'field': null,
            'terms': {},
            'total': {
                'donors': 0,
                'files': 0
            }
        };

        _.forEach(_.keys(rootField.terms), function (term) {
            const childField = rootField.terms[term];
            if (typeof retField.field === 'undefined' || !retField.field) retField.field = childField.field;

            _.forEach(_.keys(childField.terms), function (t) {
                if (typeof retField.terms[t] === 'undefined') {
                    retField.terms[t] = {
                        'files': 0,
                        'donors': 0
                    };
                }
                retField.terms[t].donors += childField.terms[t].donors;
                retField.terms[t].files += childField.terms[t].files;
                retField.total.donors += childField.terms[t].donors;
                retField.total.files += childField.terms[t].files;
            });
        });

        retField.terms = _.object(_.sortBy(_.pairs(retField.terms), function (termPair) { return termPair[1][aggregateType]; }));

        return retField;
    });

    constructor(props) {
        super(props);
        this.getFieldForLegend = this.getFieldForLegend.bind(this);
        this.updateIfShould = this.updateIfShould.bind(this);
        this.width = this.width.bind(this);
        this.height = this.height.bind(this);

        this.legendContainerRef = React.createRef();
    }

    componentDidMount() {
        this.updateIfShould();
    }

    componentDidUpdate(pastProps, pastState) {
        this.updateIfShould();
    }

    getFieldForLegend() {
        const { field, barplot_data_unfiltered, barplot_data_filtered, aggregateType, showType, termLabelTransform } = this.props;
        return Legend.barPlotFieldDataToLegendFieldsData(
            AggregatedLegend.collectSubDivisionFieldTermCounts(
                showType === 'all' ? barplot_data_unfiltered : barplot_data_filtered || barplot_data_unfiltered,
                aggregateType || 'files',
                field
            ),
            function (term) { return typeof term[aggregateType] === 'number' ? -term[aggregateType] : 'term'; },
            undefined,
            termLabelTransform
        );
    }

    /**
     * Do a forceUpdate() in case we set this.shouldUpdate = true in an initial render.
     * this.shouldUpdate would be set if legend fields do not have colors yet from cache.
     */
    updateIfShould() {
        const fieldForLegend = this.getFieldForLegend();
        const shouldUpdate = (
            fieldForLegend &&
            fieldForLegend.terms && fieldForLegend.terms.length > 0 &&
            fieldForLegend.terms[0] && fieldForLegend.terms[0].color === null
        );

        if (shouldUpdate) {
            setTimeout(() => {
                this.forceUpdate();
            }, 750);
        }
    }

    width() {
        const { width: propWidth, windowWidth } = this.props;
        if (propWidth) return propWidth;
        const elem = this.legendContainerRef.current;
        const width = elem && elem.offsetWidth;

        return width || layout.gridContainerWidth(windowWidth) * (3 / 12) - 15;
    }

    height() {
        const { height: propHeight } = this.props;
        if (propHeight) return propHeight;
        const elem = this.legendContainerRef.current;
        const height = elem && elem.offsetHeight;
        return height || null;
    }

    render() {
        const { field, barplot_data_unfiltered, isLoadingChartData, aggregateType, href, cursorDetailActions, schemas } = this.props;
        if (!field || !barplot_data_unfiltered || isLoadingChartData || (barplot_data_unfiltered.total && barplot_data_unfiltered.total.files === 0)) {
            return null;
        }

        const fieldForLegend = this.getFieldForLegend();

        return (
            <div className="legend-container-inner" ref={this.legendContainerRef}>
                <Legend {...{ href, aggregateType, cursorDetailActions }} field={fieldForLegend || null}
                    includeFieldTitles={false} schemas={schemas}
                    width={this.width()} height={this.height()} hasPopover={false}
                //expandable
                //expandableAfter={8}
                //cursorDetailActions={boundActions(this, showType)}
                />
            </div>
        );
    }
}
